import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BleClient, BleDevice } from '@capacitor-community/bluetooth-le';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { BlePermissionsDialogComponent } from '../../../modules/home/components/ble-permissions-dialog/ble-permissions-dialog.component';
import { BleActivateDialogComponent } from '../../../modules/home/components/ble-activate-dialog/ble-activate-dialog.component';
import { Router } from '@angular/router';
import { ToastService } from '../toast.service';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class NativeBluetoothService implements OnDestroy /* BluetoothAdapter */ {
  private loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private deviceSubject = new BehaviorSubject<BleDevice | null>(null);
  device$: Observable<BleDevice | null> = this.deviceSubject.asObservable();

  private enabledSubject = new BehaviorSubject<boolean>(false);
  enabled$: Observable<boolean> = this.enabledSubject.asObservable();

  private scannedDevicesSubject = new BehaviorSubject<BleDevice[]>([]);
  scannedDevices$: Observable<BleDevice[]> = this.scannedDevicesSubject.asObservable();

  private isScanningSubject = new BehaviorSubject<boolean>(false);
  isScanning$: Observable<boolean> = this.isScanningSubject.asObservable();

  private conectingSubject = new BehaviorSubject<boolean>(false);
  conecting$: Observable<boolean> = this.conectingSubject.asObservable();

  private dataReceivedSubject = new BehaviorSubject<string>('');
  dataReceived$: Observable<string> = this.dataReceivedSubject.asObservable();

  private disconnectedSubject = new BehaviorSubject<boolean>(false);
  disconnected$: Observable<boolean> = this.disconnectedSubject.asObservable();

  private scannedDevices: BleDevice[] = [];
  private readonly characteristic = '663ea70b-e351-474a-a3a0-125a0bc96725';
  private readonly uuidService = '663ea70b-e351-474a-a3a0-125a0bc96724';
  private dataReceived = '';
  private totalChunks = 0;

  constructor(
    private _dialog: MatDialog,
    private ngZone: NgZone,
    private _router: Router,
    private toastService: ToastService
  ) {
    BleClient.initialize().catch(reason => {
      this.ngZone.run(() => this.loadingSubject.next(false));
      if (reason.toString().includes('Permission denied')) {
        const dialogRef = this._dialog.open(BlePermissionsDialogComponent, {
          closeOnNavigation: false,
          disableClose: true,
          panelClass: 'app-dialog'
        });

        dialogRef.afterClosed().subscribe(resp => {
          if (resp === 'confirm') BleClient.openAppSettings();
        });
      } else {
        alert(reason);
      }
    });

    const bleEnabledHandler = (enabled: boolean) => {
      console.log('BLE STATUS', enabled, 'Platform:', Capacitor.getPlatform());

      this.ngZone.run(() => this.enabledSubject.next(enabled));

      if (!enabled && Capacitor.getPlatform() !== 'ios') {
        console.log('ABRIENDO MODAL DESDE SERVICE');

        const dialogRef = this._dialog.open(BleActivateDialogComponent, {
          closeOnNavigation: false,
          disableClose: true,
          panelClass: 'app-dialog'
        });
      }
    };

    BleClient.isEnabled().then(bleEnabledHandler);

    let isFirst = true;
    BleClient.startEnabledNotifications(enabled => {
      if (isFirst) {
        isFirst = false;
        return;
      }
      isFirst = true;

      bleEnabledHandler(enabled);
    });
  }

  async enableBle(): Promise<boolean> {
    const actived = await BleClient.isEnabled();

    if (actived) {
      return true;
    }

    if (Capacitor.getPlatform() === 'android') {
      await BleClient.requestEnable();
    }

    return BleClient.isEnabled();
  }

  async disableBle(): Promise<void> {
    if (Capacitor.getPlatform() === 'android') {
      await BleClient.disable();
    }
  }

  async scanDevices(namePrefix: string, time: number) {
    this.scannedDevices = [];

    this.ngZone.run(() => this.isScanningSubject.next(true));

    try {
      await BleClient.requestLEScan(
        {
          namePrefix,
          allowDuplicates: false
        },
        result => {
          const device: BleDevice = {
            deviceId: result.device.deviceId,
            name: result.device.name || 'Sin nombre'
          };

          if (!this.scannedDevices.find(d => d.deviceId === device.deviceId)) {
            this.scannedDevices.push(device);

            this.ngZone.run(() => {
              this.scannedDevicesSubject.next([...this.scannedDevices]);
            });
          }
        }
      );
    } catch (error) {
      console.log('scan error:', error);
    }

    setTimeout(() => {
      this.stopScanDevices();
    }, time);
  }
  async stopScanDevices() {
    await BleClient.stopLEScan();
    this.ngZone.run(() => this.isScanningSubject.next(false));
  }

  async connectDevice(device: BleDevice, times = 1): Promise<void> {
    this.ngZone.run(() => this.conectingSubject.next(true));
    this._router.navigate(['/home/ble-connect-device']);

    await BleClient.connect(device.deviceId, () => {
      this.ngZone.run(() => this.deviceSubject.next(null));
      if (!['/home/ble-init', 'ble-connect-device'].includes(this._router.url)) {
        this.toastService.show('info', 'Dispositivo desconectado');
        this._router.navigate(['/home/ble-init']);
      }
    })
      .then(() => {
        this.ngZone.run(() => {
          this.deviceSubject.next(device);
          this.disconnectedSubject.next(false);
          this.conectingSubject.next(false);
        });
      })
      .catch(async reason => {
        const isTimeOut = reason.toString().includes('Connection timeout');
        if (isTimeOut && times < 4) this.connectDevice(device, times + 1);
        else {
          this.ngZone.run(() => this.conectingSubject.next(false));
          if (isTimeOut) this._router.navigate(['/home/ble-connect-device']);
          else alert('Ocurrio un problema al intentar conectar: ' + reason.toString());
        }
      });

    BleClient.startNotifications(device.deviceId, this.uuidService, this.characteristic, data => {
      if (data.byteLength > 0) {
        const bytes = new Uint8Array(data.buffer);
        const decoder = new TextDecoder('utf-8');
        const [header, chunk] = decoder.decode(bytes).split('|');
        const [num, total] = header.split('/').map(Number);
        this.dataReceived += chunk;

        if (this.totalChunks === 0) this.totalChunks = total;

        if (num === this.totalChunks) {
          this.dataReceivedSubject.next(this.dataReceived);
          this.dataReceived = '';
          this.totalChunks = 0;
        }
      }
    });
  }

  async disconnectDevice(): Promise<void> {
    if (this.deviceSubject.value) {
      await BleClient.disconnect(this.deviceSubject.value.deviceId);
      this.ngZone.run(() => this.disconnectedSubject.next(true));
    }
  }

  connected(): boolean {
    return this.deviceSubject.value !== null;
  }

  ngOnDestroy() {
    BleClient.stopEnabledNotifications();
    const device = this.deviceSubject.value;
    if (device) {
      BleClient.stopNotifications(device.deviceId, this.uuidService, this.characteristic);
    }
  }
}
